import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';

const PRIORITY_RANK={
  NONE:0,
  LOW:1,
  REVIEW:2,
  MEDIUM:3,
  HIGH:4,
  CRITICAL:5
};

function finite(v){return typeof v==='number'&&Number.isFinite(v)}
function clamp(v,a,b){return Math.max(a,Math.min(b,v))}
function rank(x){return PRIORITY_RANK[x]||0}

function normalizeItem(x){
  x=x||{};
  return Object.freeze({
    symbol:x.symbol||null,
    scope:['OWNED','WATCHLIST','RESEARCH'].includes(x.scope)?x.scope:'RESEARCH',
    companyState:x.companyState||'UNKNOWN',
    marketAttention:x.marketAttention||'NONE',
    transmissionState:x.transmissionState||'UNKNOWN',
    exposureLevel:x.exposureLevel||'UNKNOWN',
    eventAttention:x.eventAttention||'NONE',
    contradictionState:x.contradictionState||'NONE',
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    freshnessHours:finite(x.freshnessHours)?Math.max(0,x.freshnessHours):null,
    thesisBreaker:x.thesisBreaker===true,
    owned:x.scope==='OWNED'||x.owned===true
  });
}

function freshnessFactor(hours){
  if(!finite(hours)) return .65;
  if(hours<=24) return 1;
  if(hours<=72) return .85;
  if(hours<=168) return .70;
  return .50;
}

function verificationFactor(v){
  return v==='VERIFIED'?1:v==='PARTIAL'?.65:.30;
}

function scoreItem(input){
  const x=normalizeItem(input);

  let base=0;

  // Personal/owned context matters more because it can require attention now.
  if(x.scope==='OWNED') base+=2.0;
  else if(x.scope==='WATCHLIST') base+=0.8;

  if(x.marketAttention==='CRITICAL') base+=4.0;
  else if(x.marketAttention==='HIGH') base+=3.0;
  else if(x.marketAttention==='MEDIUM') base+=1.8;
  else if(x.marketAttention==='REVIEW') base+=1.0;

  if(x.transmissionState==='NEGATIVE_PRESSURE') base+=2.2;
  else if(x.transmissionState==='MIXED') base+=1.0;
  else if(x.transmissionState==='POSITIVE_SUPPORT') base+=0.3;

  if(x.exposureLevel==='HIGH') base+=1.8;
  else if(x.exposureLevel==='MODERATE') base+=1.0;
  else if(x.exposureLevel==='UNKNOWN') base+=0.5;

  if(x.eventAttention==='HIGH') base+=2.0;
  else if(x.eventAttention==='MEDIUM') base+=1.2;
  else if(x.eventAttention==='REVIEW') base+=0.7;

  if(x.contradictionState && x.contradictionState!=='NONE' && x.contradictionState!=='NO_MAJOR_CONTRADICTION') base+=1.8;
  if(x.thesisBreaker) base+=5.0;

  const evidence=verificationFactor(x.verification);
  const freshness=freshnessFactor(x.freshnessHours);

  // Low quality data can create review pressure, but cannot inflate certainty.
  const evidenceAdjusted = base * (.55 + .45*evidence) * freshness;

  let priority='NONE';
  if(x.thesisBreaker && x.scope==='OWNED') priority='CRITICAL';
  else if(evidenceAdjusted>=6.5) priority='HIGH';
  else if(evidenceAdjusted>=4.0) priority='MEDIUM';
  else if(evidenceAdjusted>=2.0) priority='REVIEW';
  else if(evidenceAdjusted>0) priority='LOW';

  if(x.verification==='UNVERIFIED' && rank(priority)>rank('REVIEW') && !x.thesisBreaker){
    priority='REVIEW';
  }

  return Object.freeze({
    symbol:x.symbol,
    scope:x.scope,
    priority,
    rawScore:base,
    score:Number(evidenceAdjusted.toFixed(3)),
    verification:x.verification,
    freshnessHours:x.freshnessHours,
    thesisBreaker:x.thesisBreaker,
    tradeSignal:false,
    companyQualityOverride:false
  });
}

function whyNow(input){
  const x=normalizeItem(input);
  const reasons=[];

  if(x.thesisBreaker) reasons.push('THESIS_BREAKER');
  if(x.marketAttention==='HIGH'||x.marketAttention==='CRITICAL') reasons.push('GLOBAL_MARKET_CHANGE');
  if(x.transmissionState==='NEGATIVE_PRESSURE') reasons.push('NEGATIVE_TRANSMISSION');
  if(x.exposureLevel==='HIGH') reasons.push('MATERIAL_EXPOSURE');
  if(x.eventAttention==='HIGH') reasons.push('NEAR_TERM_EVENT');
  if(x.contradictionState && x.contradictionState!=='NONE' && x.contradictionState!=='NO_MAJOR_CONTRADICTION') reasons.push('CONTRADICTION');
  if(x.verification==='UNVERIFIED') reasons.push('VERIFICATION_GAP');
  if(!reasons.length) reasons.push('NO_URGENT_DRIVER');

  return Object.freeze(reasons.slice(0,4));
}

function prioritize(items){
  const rows=Array.isArray(items)?items:[];
  const scored=rows.map(x=>{
    const s=scoreItem(x);
    return Object.freeze({...s,whyNow:whyNow(x)});
  }).sort((a,b)=>{
    const r=rank(b.priority)-rank(a.priority);
    return r!==0?r:b.score-a.score;
  });

  return Object.freeze(scored);
}

function compress(items,maxItems){
  const n=finite(maxItems)?Math.max(1,Math.floor(maxItems)):5;
  const ranked=prioritize(items);
  const actionable=ranked.filter(x=>x.priority!=='NONE'&&x.priority!=='LOW');

  return Object.freeze({
    top:Object.freeze(actionable.slice(0,n)),
    hiddenCount:Math.max(0,actionable.length-n),
    totalReviewed:ranked.length,
    criticalCount:ranked.filter(x=>x.priority==='CRITICAL').length,
    highCount:ranked.filter(x=>x.priority==='HIGH').length,
    mediumCount:ranked.filter(x=>x.priority==='MEDIUM').length,
    reviewCount:ranked.filter(x=>x.priority==='REVIEW').length,
    tradeSignal:false
  });
}

function personalFirst(items){
  const ranked=prioritize(items);
  return Object.freeze([
    ...ranked.filter(x=>x.scope==='OWNED'),
    ...ranked.filter(x=>x.scope!=='OWNED')
  ]);
}

function decisionContext(input){
  const x=normalizeItem(input);
  const s=scoreItem(x);

  let state='NO_ACTION';
  if(s.priority==='CRITICAL') state='REVIEW_NOW';
  else if(s.priority==='HIGH') state='REVIEW_SOON';
  else if(s.priority==='MEDIUM') state='INVESTIGATE';
  else if(s.priority==='REVIEW') state='VERIFY';

  return Object.freeze({
    symbol:x.symbol,
    scope:x.scope,
    state,
    priority:s.priority,
    reasons:whyNow(x),
    tradeSignal:false,
    capitalPermission:false,
    entryGateOverride:false,
    companyQualityOverride:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const ownedCritical={
    symbol:'AAA',scope:'OWNED',marketAttention:'HIGH',
    transmissionState:'NEGATIVE_PRESSURE',exposureLevel:'HIGH',
    eventAttention:'HIGH',contradictionState:'STRONG_COMPANY_EXTERNAL_PRESSURE',
    verification:'VERIFIED',freshnessHours:4,thesisBreaker:true
  };
  const watchHigh={
    symbol:'BBB',scope:'WATCHLIST',marketAttention:'HIGH',
    transmissionState:'NEGATIVE_PRESSURE',exposureLevel:'HIGH',
    verification:'VERIFIED',freshnessHours:4
  };
  const unverifiedHot={
    symbol:'CCC',scope:'OWNED',marketAttention:'HIGH',
    transmissionState:'NEGATIVE_PRESSURE',exposureLevel:'HIGH',
    verification:'UNVERIFIED',freshnessHours:2
  };
  const stale={
    symbol:'DDD',scope:'OWNED',marketAttention:'MEDIUM',
    transmissionState:'MIXED',exposureLevel:'MODERATE',
    verification:'VERIFIED',freshnessHours:240
  };

  let s=scoreItem(ownedCritical);
  ok('owned thesis breaker becomes critical',s.priority==='CRITICAL');
  ok('critical item remains non-trading',s.tradeSignal===false);
  ok('critical item cannot override company quality',s.companyQualityOverride===false);

  s=scoreItem(unverifiedHot);
  ok('unverified hot signal is capped at review',s.priority==='REVIEW');

  const rs=whyNow(ownedCritical);
  ok('thesis breaker reason appears first',rs[0]==='THESIS_BREAKER');
  ok('why-now reasons are compressed',rs.length<=4);

  const p=prioritize([watchHigh,ownedCritical,stale,unverifiedHot]);
  ok('critical item ranks first',p[0].symbol==='AAA');
  ok('priority sorting respects critical',p[0].priority==='CRITICAL');

  const pf=personalFirst([watchHigh,stale]);
  ok('personal-first puts owned before watchlist',pf[0].scope==='OWNED');

  const c=compress([watchHigh,ownedCritical,stale,unverifiedHot],2);
  ok('compression caps top list',c.top.length===2);
  ok('compression reports hidden actionable items',c.hiddenCount>=1);
  ok('compression remains non-trading',c.tradeSignal===false);

  const d=decisionContext(ownedCritical);
  ok('critical maps to review-now',d.state==='REVIEW_NOW');
  ok('decision context never grants capital permission',d.capitalPermission===false);
  ok('decision context never bypasses entry gate',d.entryGateOverride===false);
  ok('decision context never overrides company quality',d.companyQualityOverride===false);

  const low=decisionContext({
    symbol:'EEE',scope:'RESEARCH',verification:'VERIFIED',freshnessHours:12
  });
  ok('quiet research item remains no-action',low.state==='NO_ACTION');

  const fresh=freshnessFactor(12), old=freshnessFactor(300);
  ok('fresh evidence weighs more than stale evidence',fresh>old);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={
  VERSION,PRIORITY_RANK,normalizeItem,freshnessFactor,verificationFactor,
  scoreItem,whyNow,prioritize,compress,personalFirst,decisionContext,selfTest
};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestWhatMattersNowEngine=Object.freeze(API);

})(engineContext);
